import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { contain } from 'intrinsic-scale';
import { unique } from '../../util/array';
import articles from '../../services/products/articles';
import products from '../../services/products';
import productService from '../../services/products';
import swiperService from '../../services/swiper';
import emitter from '../../emitter';
import constants from '../../settings/constants';
import { DROP_ITEM, GRAB_ITEM } from '../../settings/events';
import * as supportedEvents from '../../util/supportedEvents';
import * as alignments from '../Popup/alignments';
import Popup from '../Popup';
import popupStyles from '../Popup/Popup.module.less';
import BrorTransition from '../Transition/Transition';
import styles from './ProductSwiperItem.module.less';
import ProductSwiperIndicator from './ProductSwiperIndicator';
import platform from '../../util/platform';
import {
  selectCurrentColorFilter,
  selectCurrentFilterName,
  selectMeasurementString,
  selectProductMenuFilter,
  selectProductMenuLayout,
  selectShouldDisplayMeasurements,
} from '../../state/productMenu/productMenuSelectors.ts';
import { translate } from '../../services/L10n';
import { withLigatures } from '../../util/measures';
import {
  selectIsRtl,
  selectUseMetric,
} from '../../state/dexfSettings/dexfSettingsSelectors';
import { Icon } from '../Icon';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';
import { FILTERS } from '../../constants';
import { applicationSettings } from '../../settings/application';
import { t } from '../../translations';

const actionThreshold = 10;
let clicked = false;

class ProductSwiperItem extends React.Component {
  static propTypes = {
    forwardEvent: PropTypes.func.isRequired,
    product: PropTypes.object.isRequired,
    mode: PropTypes.oneOf(Object.values(constants.DRAG_MODE)).isRequired,
    isVertical: PropTypes.bool.isRequired,
    filter: PropTypes.object.isRequired,
    userAgent: PropTypes.object.isRequired,
    disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
      .isRequired,
    currentColorFilter: PropTypes.object,
    renderMultiline: PropTypes.bool.isRequired,
    displayMeasurement: PropTypes.bool.isRequired,
    swiperLayout: PropTypes.string,
    measurementString: PropTypes.string,
    isRtl: PropTypes.bool.isRequired,
    useMetricMeasures: PropTypes.bool.isRequired,
  };

  root = React.createRef();
  imageDiv = React.createRef();
  preventSwipeTriggerClick = false;

  action = {
    startPos: {},
    mode: false,
  };

  state = {
    errorVisible: false,
  };

  constructor(props) {
    super(props);

    const { product, filter, currentColorFilter } = props;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onClick = this.onClick.bind(this);

    this.shouldDisplayProductType =
      swiperService.shouldDisplayProductType(product);

    const idOfArticleToGet = productService.isOnlyAvailableInMultipack(
      product.id
    )
      ? constants.BULK_ARTICLES[product.id].id
      : product.id;
    this.article = articles.getArticleContent(idOfArticleToGet);

    const includedArticles = products.getIncludedArticles(product.id);
    while (
      includedArticles.length &&
      (!this.article ||
        (!this.article.typeName && !this.article.measureReference))
    ) {
      this.article = articles.getArticleContent(includedArticles.shift());
    }
    this.typeText = swiperService.getTypeText(this.article, product);

    this.imageObj = productService.getArticleImage(product.id);
    this.shouldDisplayColor = swiperService.displayColor(
      product,
      filter,
      currentColorFilter
    );

    this.layouts = {
      pairLayout: () => [this.renderPairTitle(), this.renderImage()],
      default: () => [this.renderImage(), this.renderDetails()],
      rowLayout: () => [this.renderImage(), this.renderDetails()],
      combinedLayout: () => [this.renderImage(), this.renderCombinedLayout()],
      centeredLayout: () => [this.renderImage(), this.renderCenteredText()],
    };

    this.indicator =
      filter.indicators &&
      filter.indicators.find(indicator => indicator.id === product.id);
  }

  componentDidMount() {
    // a native event is used because calling preventDefault to prevent scroll in iOS
    // does not work when using react events (even with calling e.nativeEvent.preventDefault())
    if (supportedEvents.TOUCH_SUPPORT) {
      this.root.current.addEventListener('touchmove', this.onPointerMove);
    }

    emitter.on(DROP_ITEM, this.resetAction);
  }

  componentDidUpdate() {
    if (this.state.errorVisible && !this.props.disabled) {
      this.setState({
        errorVisible: false,
      });
    }
  }

  resetAction = () => {
    this.preventSwipeTriggerClick = setTimeout(() => {
      this.action = {
        startPos: {},
        mode: false,
      };
    }, 10);
  };

  componentWillUnmount() {
    this.root.current.removeEventListener('touchmove', this.onPointerMove);
    if (this.preventSwipeTriggerClick) {
      clearTimeout(this.preventSwipeTriggerClick);
    }

    emitter.off(DROP_ITEM, this.resetAction);
  }

  onPointerDown(e) {
    this.action.startPos = {
      x: e.clientX || e.touches[0].clientX,
      y: e.clientY || e.touches[0].clientY,
    };
  }

  onPointerMove(e) {
    const isVertical = this.props.isVertical;
    if (this.action.mode || this.state.errorVisible) {
      if (this.action.mode === 'dragging' && e.type === 'touchmove') {
        this.props.forwardEvent(e);
      }

      return;
    }

    const pos = {
      x: e.clientX || e.touches[0].clientX,
      y: e.clientY || e.touches[0].clientY,
    };

    const dx = Math.abs(pos.x - this.action.startPos.x);
    const dy = Math.abs(pos.y - this.action.startPos.y);
    // change how much we care about moving in the swipe direction,
    const angle = 130; // degrees
    const swipeWeight = Math.atan((Math.PI / 180) * ((180 - angle) / 2));

    if (
      (isVertical && dx > actionThreshold && dx > dy * swipeWeight) ||
      (!isVertical && dy > actionThreshold && dy > dx * swipeWeight)
    ) {
      if (this.props.disabled) {
        localStatisticsReporter.reportProductMenuSwiperInteraction(
          { status: 'disabled', item: this.props.product.id },
          'drag'
        );

        return this.setState({
          errorVisible: true,
        });
      }

      this.action.mode = 'dragging';
      const img = e.currentTarget.querySelector('img');
      const trimmedThumb = swiperService.getThumbnailData(
        this.getImageUrl(this.imageObj)
      );

      // Since we are using object-fit: contain we need to calculate how large the actual image is
      const containedSize = contain(
        this.imageDiv.current.clientWidth,
        this.imageDiv.current.clientHeight,
        img.naturalWidth,
        img.naturalHeight
      );

      const imgScale = containedSize.width
        ? containedSize.width / img.naturalWidth
        : 1;

      const domSize = {
        height: imgScale * trimmedThumb.height,
        width: imgScale * trimmedThumb.width,
      };

      emitter.emit(
        GRAB_ITEM,
        this.props.product,
        pos,
        this.props.mode,
        e.type,
        domSize
      );
    } else if (
      (isVertical && dy > actionThreshold && dy > dx) ||
      (!isVertical && dx > actionThreshold && dx > dy)
    ) {
      this.action.mode = 'swiping';
      localStatisticsReporter.reportProductMenuSwiperInteraction({}, 'swipe');
    }
  }

  onClick() {
    if (clicked || this.action.mode === 'swiping') {
      return;
    }
    localStatisticsReporter.reportProductMenuSwiperInteraction(
      { item: this.props.product.id },
      'click'
    );

    clicked = this.props.product.id;
    this.forceUpdate();
    this.clickHelpActive = setTimeout(() => {
      clicked = false;
      this.forceUpdate();
    }, 600);
  }

  getDimensionGroup(dimension, article, product) {
    const headerLabelKey = `SHORT_LABEL_${dimension.toUpperCase()}`;
    return (
      <React.Fragment>
        <span className={classNames(styles.dimensionHeader)}>
          {translate(t[headerLabelKey])}
        </span>{' '}
        {this.getCombinedMeasurements(dimension, article, product)}
      </React.Fragment>
    );
  }

  onPopupClose = () => {
    this.setState({
      errorVisible: false,
    });
  };

  getImageUrl = image => {
    const { isVertical, isRtl, userAgent } = this.props;
    const { isMobile } = userAgent;

    const fallbackUrl = image.thumbnailUrl || image.url;

    if (!isVertical) {
      if (isMobile) {
        return (
          image.thumbnailUrlMobilePortrait ||
          image.thumbnailUrlPortrait ||
          fallbackUrl
        );
      }
      return image.thumbnailUrlPortrait || fallbackUrl;
    } else if (isRtl) {
      if (isMobile) {
        return (
          image.thumbnailUrlMobileLandscape ||
          image.thumbnailUrlRTL ||
          fallbackUrl
        );
      }
      return image.thumbnailUrlRTL || fallbackUrl;
    } else if (isMobile && isVertical) {
      return image.thumbnailUrlMobileLandscape || fallbackUrl;
    }

    return fallbackUrl;
  };

  capitalize = (str, locale) => {
    const languageCode = locale.substring(0, 2);
    const capsLetters = languageCode === 'nl' && /^ij/i.test(str) ? 2 : 1;

    let capitalizedStr;
    try {
      capitalizedStr =
        str.substring(0, capsLetters).toLocaleUpperCase(locale) +
        str.substring(capsLetters);
    } catch (e) {
      capitalizedStr = str.substring(0, 1).toUpperCase() + str.substring(1);
    }

    return capitalizedStr;
  };

  getImageStyle = () => {
    const { filter, userAgent } = this.props;

    if (filter.swiperLayout.verticalImgWidths?.[userAgent.deviceType]) {
      return {
        flex: `0 0 ${
          filter.swiperLayout.verticalImgWidths[
            platform.isKiosk ? 'kiosk' : userAgent.deviceType
          ]
        }px`,
      };
    }
  };

  /**
   * Return classes for images
   * @returns {*}
   */
  getImageClassNames = () => {
    if (
      this.props.swiperLayout === 'centeredLayout' &&
      this.props.isSectionFilter
    )
      return styles.img_centered_layout;
  };

  /**
   * Render image
   *
   * @returns {*|JSX.Element}
   */
  renderImage = () =>
    this.imageObj && (
      <div
        key={'productSwiperItemImage'}
        className={classNames(
          styles.image,
          this.indicator?.offset && styles.indicatorOffset
        )}
        ref={this.imageDiv}
        style={this.getImageStyle()}
      >
        <img
          src={this.getImageUrl(this.imageObj)}
          alt={this.typeText}
          className={this.getImageClassNames()}
        />
        {this.indicator && (
          <ProductSwiperIndicator indicator={this.indicator} />
        )}
      </div>
    );

  /**
   * Returns formatted article text
   *
   * @returns {string|*}
   */
  getArticleText = () => {
    const productTypeText =
      productService.getCustomProductTypeText(this.props.product) ||
      this.typeText;
    return this.shouldDisplayColor
      ? `${productTypeText}, ${this.article.validDesignText}`
      : productTypeText;
  };

  /**
   * Renders details
   *
   * @returns {JSX.Element|boolean}
   */
  renderDetails = () =>
    (this.shouldDisplayProductType || this.props.displayMeasurement) &&
    this.renderDefaultLayout();

  /**
   * Renders a combined layout.
   * @example Tables with legs need to display different measurements and the product name.
   * @returns {JSX.Element}
   */

  renderCombinedLayout = () => (
    <div className={classNames(styles.text)} key="renderCombinedLayout">
      <div className={classNames(styles.productNameText)}>
        <b>{this.article.name}</b>{' '}
        {this.capitalize(
          this.article.validDesignText,
          applicationSettings.locale
        )}
      </div>

      <div className={classNames(styles.measurementLineBreakFix)}>
        {this.getDimensionGroup('width', this.article, this.props.product)}{' '}
        {this.getDimensionGroup('depth', this.article, this.props.product)}{' '}
        {this.getDimensionGroup('height', this.article, this.props.product)}{' '}
      </div>
    </div>
  );

  getCombinedMeasurements = (dimension, article, product) => {
    const { useMetricMeasures } = this.props;
    const { products } = this.props.filter;

    const linkedProducts = products
      .find(products => products.includes(product.id))
      .map(linkedProductId => productService.getProduct(linkedProductId))
      .filter(Boolean);

    const dimensionTexts = linkedProducts
      .map(linkedProduct =>
        withLigatures(
          swiperService.getTableDisplayText(
            article,
            linkedProduct,
            useMetricMeasures,
            dimension
          )
        )
      )
      .filter(unique);
    const dimensionElements = dimensionTexts.map((text, index) => (
      <span key={text}>{index === 0 ? text : ', ' + text}</span>
    ));

    return <span>{dimensionElements}</span>;
  };

  /**
   * Returns a string containing measurements string according to settings
   *
   * @returns {string|string}
   */
  getMeasurementString = () =>
    this.props.displayMeasurement ? this.props.measurementString : null;

  /**
   * Render product measurements
   *
   * @param renderMultiline {boolean}
   * @returns {*|JSX.Element}
   */
  renderProductMeasurements = renderMultiline =>
    renderMultiline ? (
      <div className={classNames(styles.dimensions)}>
        {this.getMeasurementString()}
      </div>
    ) : (
      this.getMeasurementString()
    );

  renderCenteredText = () =>
    (this.shouldDisplayProductType || this.props.displayMeasurement) &&
    this.renderDefaultLayout(true);

  /**
   * Get default layout classes
   * @param centered
   * @returns {*[]}
   */
  getDefaultLayoutClasses = centered => [
    styles.text,
    centered && styles.text_centered_layout,
  ];

  /**
   * Render default layout
   *
   * @returns {JSX.Element}
   */
  renderDefaultLayout = (centered = false) => (
    <div
      className={classNames(this.getDefaultLayoutClasses(centered))}
      key="defaultLayout"
    >
      {!this.shouldDisplayProductType &&
        (this.shouldDisplayColor || this.props.renderMultiline) && (
          <div className={classNames(styles.colorText)}>
            {this.shouldDisplayColor
              ? this.capitalize(
                  this.article.validDesignText,
                  applicationSettings.locale
                )
              : ''}
          </div>
        )}

      {this.shouldDisplayProductType && (
        <div className={classNames(styles.mainText)}>
          {this.getArticleText()}
        </div>
      )}

      {this.renderProductMeasurements(this.props.renderMultiline)}
    </div>
  );

  /**
   * Render paired layout
   *
   * @returns {JSX.Element}
   */
  renderPairTitle = () => (
    <div className={classNames(styles.pairedHeader)} key="pair-title">
      {this.getMeasurementString()}
    </div>
  );

  /**
   * Selects and returns correct layout format
   *
   * @returns {*}
   */
  renderLayout = () => this.layouts[this.props.swiperLayout]();

  /**
   * Return Item classes
   * @returns {(*|false)[]}
   */
  getItemClasses = () => {
    const {
      disabled,
      product: { id },
      filter: { itemClass },
      swiperLayout,
    } = this.props;
    return [
      styles.item,
      disabled && styles.disabled,
      clicked === id && styles.clicked,
      styles[itemClass],
      styles[swiperLayout],
      swiperLayout === 'centeredLayout' && styles.item_centered_layout,
    ];
  };

  render() {
    const { isVertical, disabled } = this.props;

    const disabledMessage = (' ' + disabled).slice(1); // Lose the reference to the string IKEABROR-342

    return (
      <div
        ref={this.root}
        onPointerDown={
          supportedEvents.POINTER_SUPPORT ? this.onPointerDown : null
        }
        onMouseDown={supportedEvents.MOUSE_SUPPORT ? this.onPointerDown : null}
        onTouchStart={supportedEvents.TOUCH_SUPPORT ? this.onPointerDown : null}
        onPointerMove={
          supportedEvents.POINTER_SUPPORT ? this.onPointerMove : null
        }
        onMouseMove={supportedEvents.MOUSE_SUPPORT ? this.onPointerMove : null}
        onClick={disabled ? null : this.onClick}
        className={classNames(this.getItemClasses())}
      >
        {this.renderLayout()}
        {disabled && (
          <BrorTransition
            classNames={popupStyles}
            in={!!this.state.errorVisible}
          >
            <Popup
              target={this.imageDiv.current}
              dismissible
              onClose={this.onPopupClose}
              alignment={isVertical ? alignments.LEFT : alignments.TOP}
            >
              <Icon className={popupStyles.icon} iconName="IncorrectIcon" />
              <div>{disabledMessage}</div>
            </Popup>
          </BrorTransition>
        )}
      </div>
    );
  }
}

export default connect((state, { product }) => ({
  currentColorFilter: selectCurrentColorFilter(state),
  measurementString: selectMeasurementString(state, product.id),
  displayMeasurement: selectShouldDisplayMeasurements(state, product),
  filter: selectProductMenuFilter(state),
  swiperLayout: selectProductMenuLayout(state),
  isSectionFilter: selectCurrentFilterName(state) === FILTERS.SECTIONS,
  isRtl: selectIsRtl(state),
  useMetricMeasures: selectUseMetric(state),
}))(ProductSwiperItem);
